import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authService } from "@/services/authService";
import { convexService } from "@/services/convexService";
import { connectionManager } from "@/services/connectionManager";
import { agiService } from "@/services/agiService";
import type { LogEntry, Settings, SystemMode, AuthLevel } from "@/types";
import type { Notification } from "@/components/NotificationSystem";

// Initialize Convex service if URL is available
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL as string;
if (CONVEX_URL) {
  convexService.initialize(CONVEX_URL);
  authService.setConvexUrl(CONVEX_URL);
}

interface DashboardState {
  // UI State
  activeTab: string;
  sidebarOpen: boolean;
  theme: "dark" | "light" | "system";

  // Auth State
  authLevel: AuthLevel;
  authMethod: "password" | "passkey" | null;

  // System Mode
  systemMode: SystemMode;
  availableModes: SystemMode[];

  // Health Status
  health: any;
  healthStats: any;

  // Settings
  settings: Settings;

  // Logs
  logs: LogEntry[];

  // Notifications
  notifications: Notification[];

  // UI Panels
  agiPanelOpen: boolean;
  backendPanelOpen: boolean;

  // Actions
  setActiveTab: (tab: string) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: "dark" | "light" | "system") => void;
  toggleTheme: () => void;

  setSystemMode: (mode: SystemMode) => void;
  updateHealth: (health: any) => void;

  updateSettings: (settings: Partial<Settings>) => void;
  resetSettings: () => void;

  addLogEntry: (entry: Omit<LogEntry, "id" | "timestamp">) => void;
  clearLogs: () => void;

  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  setAGIPanelOpen: (open: boolean) => void;
  setBackendPanelOpen: (open: boolean) => void;

  initialize: () => void;
}

const defaultSettings: Settings = {
  // Backend URLs
  kernelUrl: (import.meta.env as any)['VITE_KERNEL_URL'] || "http://127.0.0.1:3340",
  convexUrl: (import.meta.env as any)['VITE_CONVEX_URL'] || "",

  // LLM API Keys and Configuration
  glmApiKey: (import.meta.env as any)['VITE_GLM_API_KEY'] || "",
  glmBaseUrl: "https://api.z.ai/api/coding/paas/v4",
  glmModel: "glm-4.7",
  deepseekApiKey: (import.meta.env as any)['VITE_DEEPSEEK_API_KEY'] || "",
  deepseekBaseUrl: "https://apis.iflow.cn/v1",
  deepseekModel: "deepseek-r1",
  qwenApiKey: (import.meta.env as any)['VITE_QWEN_API_KEY'] || "",
  qwenBaseUrl: "https://apis.iflow.cn/v1",
  qwenModel: "qwen3-coder-plus",
  openaiApiKey: (import.meta.env as any)['VITE_OPENAI_API_KEY'] || "",

  // MoM Router Settings
  enableFallbackModels: true,
  parallelModelQueries: false,

  // MCP Servers
  mcpServers: [],

  // Mode Preferences
  preferredMode: "hybrid",
  autoSwitchMode: false,
  hybridFallback: "kernel",

  // UI Preferences
  sidebarOpen: true,
  theme: "dark",
  compactMode: false,

  // Security
  sessionTimeout: 4,
  requireReauthAfter: 12,

  // Runtime settings
  maxConcurrentRequests: 5,
  requestTimeout: 30000,
  patternCacheSize: 1000,
  enablePatternBypass: true,

  // Discovery settings
  discoveryInterval: 30,
  discoveryEnabled: true,
};

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      // Initial State
      activeTab: "dashboard",
      sidebarOpen: true,
      theme: "dark",
      authLevel: "none",
      authMethod: null,
      systemMode: "hybrid",
      availableModes: [],
      health: null,
      healthStats: null,
      settings: defaultSettings,
      logs: [],
      notifications: [],
      agiPanelOpen: false,
      backendPanelOpen: false,

      // UI Actions
      setActiveTab: (tab: string) => set({ activeTab: tab }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
      setTheme: (theme: "dark" | "light" | "system") => set({ theme }),
      toggleTheme: () => set((state) => ({ theme: state.theme === "dark" ? "light" : "dark" })),

      // System Mode Actions
      setSystemMode: (mode: SystemMode) => {
        set({ systemMode: mode });
        agiService.setMode(mode);
        connectionManager.setMode(mode);
      },

      // Health Actions
      updateHealth: (health: any) => {
        const stats = {
          total: health.kernel ? 1 : 0 + health.convex ? 1 : 0 + health.mcpServers?.length || 0 + health.llmProviders?.length || 0,
          connected: (health.kernel?.connected ? 1 : 0) + (health.convex?.connected ? 1 : 0) + (health.mcpServers?.filter((s: any) => s.connected).length || 0) + (health.llmProviders?.filter((p: any) => p.healthy).length || 0),
        };
        set({ health, healthStats: stats });
      },

      // Settings Actions
      updateSettings: (newSettings: Partial<Settings>) => {
        const updated = { ...get().settings, ...newSettings };
        set({ settings: updated });

        // Update connection manager config
        connectionManager.updateConfig({
          kernelUrl: updated.kernelUrl,
          convexUrl: updated.convexUrl,
          mcpServers: updated.mcpServers,
          llmKeys: [
            { provider: "GLM", apiKey: updated.glmApiKey },
            { provider: "DeepSeek", apiKey: updated.deepseekApiKey },
            { provider: "Qwen", apiKey: updated.qwenApiKey },
            { provider: "OpenAI", apiKey: updated.openaiApiKey },
          ].filter((k) => !!k.apiKey),
        });

        // Update Convex service
        if (updated.convexUrl) {
          // convexService.initialize(updated.convexUrl);
        }
      },

      resetSettings: () => {
        set({ settings: defaultSettings });
        // Apply default settings to services
        get().updateSettings({});
      },

      // Log Actions
      addLogEntry: (entry) => {
        const newLog: LogEntry = {
          id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          ...entry,
        };
        set((state) => ({
          logs: [newLog, ...state.logs].slice(0, 1000), // Keep last 1000 logs
        }));
      },

      clearLogs: () => set({ logs: [] }),

      // Notification Actions
      addNotification: (notification: Notification) => {
        set((state) => ({
          notifications: [...state.notifications, notification],
        }));
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      // Panel Actions
      setAGIPanelOpen: (open: boolean) => set({ agiPanelOpen: open }),
      setBackendPanelOpen: (open: boolean) => set({ backendPanelOpen: open }),

      // Initialize
      initialize: () => {
        const state = get();

        // Initialize services with current settings
        connectionManager.updateConfig({
          kernelUrl: state.settings.kernelUrl,
          convexUrl: state.settings.convexUrl,
          mcpServers: state.settings.mcpServers,
          llmKeys: [
            { provider: "GLM", apiKey: state.settings.glmApiKey },
            { provider: "DeepSeek", apiKey: state.settings.deepseekApiKey },
            { provider: "Qwen", apiKey: state.settings.qwenApiKey },
            { provider: "OpenAI", apiKey: state.settings.openaiApiKey },
          ].filter((k) => !!k.apiKey),
        });

        // Start health monitoring
        connectionManager.start(state.settings.discoveryInterval);

        // Subscribe to health updates
        connectionManager.subscribe((health) => {
          state.updateHealth(health);
        });

        // Subscribe to auth changes
        authService.subscribe(() => {
          const level = authService.getAuthLevel();
          set({ authLevel: level });
        });

        // Initial auth state
        set({ authLevel: authService.getAuthLevel() });

        // Add initialization log
        get().addLogEntry({
          category: "system",
          action: "Dashboard Initialized",
          details: "Control center started successfully",
          severity: "info",
          source: "dashboard",
        });
      },
    }),
    {
      name: "binetic-dashboard-storage",
      partialize: (state) => ({
        // Persist only these fields
        settings: state.settings,
        logs: state.logs.slice(0, 500), // Limit persisted logs
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        systemMode: state.systemMode,
        preferredMode: state.settings.preferredMode,
      }),
    }
  )
);
